<template>
  <div>
    <p class="mb-4 text-lg">
      {{ t('app.payments.standingApproval.subTitle') }}
    </p>
    <div
      class="absolute left-0 top-0 z-10 flex h-full w-full items-center justify-center rounded-lg bg-yapgray-300/80 dark:bg-yapdarkgray-700/80"
      v-if="state.loading && (!state.schema || !state.formAction)"
    >
      <YapLoader :size="YapLoaderSizes.Medium" />
    </div>
    <YapForm
      v-if="state.schema && state.formAction"
      :schema="state.schema"
      :uri="state.formAction.uri"
      :method="state.formAction.method"
      :data="state.form"
      :submit-label="t('dict.label.save')"
      :submit-disabled="state.form.type === StandingApprovalType.None && !biller.standingApprovals"
      @submitted="handleSubmitted"
      @errors="handleSubmissionError"
    >
      <template #form="{ validationErrors }">
        <YapAccordion
          :items="items"
          :custom-select="true"
          :selected-item="state.form.type"
          @select-item="selectItem($event)"
        >
          <template #trigger="{ item }">
            <div class="flex gap-4">
              <div
                class="flex aspect-square h-5 w-5 items-center justify-center rounded-full border-2 border-primary-500 dark:border-primary-dark-500"
              >
                <div
                  v-if="state.form.type === item.key"
                  class="aspect-square h-3 w-3 rounded-full bg-primary-500 dark:bg-primary-dark-500"
                ></div>
              </div>
              {{ item.title }}
            </div>
          </template>
          <template #default="{ item }">
            <div class="grid grid-cols-2 gap-2">
              <YapLabeledInput
                name="amount"
                label="amount in CHF"
                :schema="state.schema.properties.amount"
                v-model="state.form.amount"
                :errors="validationErrors"
                required
              />
              <YapLabeledInput
                name="period"
                :schema="state.schema.properties.period"
                v-model="state.form.period"
                :errors="validationErrors"
                required
              />
            </div>
          </template>
        </YapAccordion>
        <p class="mt-4 text-center text-sm">{{ t('app.payments.standingApproval.info') }}</p>
      </template>
    </YapForm>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { reactive, onMounted, computed } from 'vue';

import { useI18n } from '@/plugins/i18n';
import YapForm from '@/components/form/YapForm.vue';
import { useSchemasStore } from '@/store/schemaStore';
import YapAccordion from '@/components/YapAccordion.vue';
import { Action, Link, Method, Schema } from '@/types/general';
import { useCashAccountsStore } from '@/store/cashAccountsStore';
import { useContextSwitchStore } from '@/store/contextSwitchStore';
import YapLabeledInput from '@/components/form/YapLabeledInput.vue';
import YapLoader, { YapLoaderSizes } from '@/components/YapLoader.vue';
import { Biller, StandingApprovalPeriod, StandingApprovalType } from '@/components/ebill/types/biller';

const props = defineProps<{
  biller: Biller;
  relatedBillerUid?: string;
}>();

const emits = defineEmits(['saved']);

const { t } = useI18n();
const cashAccountsStore = useCashAccountsStore();
const contextSwitchStore = useContextSwitchStore();
const { getCurrentCashAccount } = storeToRefs(cashAccountsStore);

interface State {
  loading: boolean;
  schema?: Schema;
  form: {
    currency: 'CHF';
    amount: string;
    type: StandingApprovalType;
    period: StandingApprovalPeriod;
  };
  formAction?: Action;
  standingApprovalFromProp?: {
    amount: string;
    currency: string;
    id: string;
    period: StandingApprovalPeriod;
    type: StandingApprovalType;
    _actions: {
      delete?: Action;
      edit?: Action;
    };
    _links: {
      self: Link;
    };
  };
}

const state: State = reactive({
  loading: false,
  schema: undefined,
  standingApprovalFromProp: computed(() => props.biller.standingApprovals && props.biller.standingApprovals[0]),
  form: {
    currency: 'CHF',
    amount: '',
    type: StandingApprovalType.None,
    period: StandingApprovalPeriod.None,
    iban: contextSwitchStore.isPrivateContext ? undefined : getCurrentCashAccount.value?.iban
  },
  formAction: computed(() => {
    if (state.form.type === StandingApprovalType.None && !!props.biller.standingApprovals) {
      return props.biller.standingApprovals[0]?._actions.delete;
    }
    return (
      state.standingApprovalFromProp?._actions.edit ??
      props.biller._actions?.addStandingApproval ?? {
        uri: `/related-billers/${props.relatedBillerUid}/standing-approvals`,
        method: Method.post
      }
    );
  })
});

const items = [
  {
    key: '',
    title: t('app.payments.standingApproval.none.label'),
    noDisclosePanel: true
  },
  {
    key: 'upperLimit',
    title: t('webapp.payments.standingApproval.upperLimit.label')
  },
  {
    key: 'exactAmount',
    title: t('webapp.payments.standingApproval.exactAmount.label')
  }
];

const schemaStore = useSchemasStore();
onMounted(async () => {
  if (props.biller.standingApprovals) {
    state.form.period = props.biller.standingApprovals[0].period;
    state.form.type = props.biller.standingApprovals[0].type;
    state.form.amount = props.biller.standingApprovals[0].amount;
  }
  if (!state.formAction?.uri) {
    return;
  }
  if (state.formAction?.uri) {
    state.loading = true;
    state.schema = await schemaStore.getSchemaForAction(state.formAction);
    state.loading = false;
  }
});

function handleSubmitted() {
  emits('saved');
}

function handleSubmissionError() {
  console.warn('Somthing went wrong... do we have to handle it here?');
}

function selectItem(itemKey: StandingApprovalType) {
  state.form.type = itemKey;
}
</script>
